import { useState } from 'react'
import { useArcTranslation } from 'hooks'
import { useFeatureToggle } from 'hooks'
import { AIApiResponse, GeneratedProjectData } from '../components/AIGeneratorModal/interfaces'
import { askAI, getGroupCurriculum, AIGenerationPayload, AIGenerationPayloadLegacy } from '../service/projects'
import { askAI as askAIArc } from '../service/TEMP_n8nDirect'
import { GetMyGroupsProps } from '../service/interfaces'

// ==================== FUNÇÕES LEGACY (main) ====================

const convertApiResponseToFormDataLegacy = (apiResponse: AIApiResponse, t: (key: string) => string): GeneratedProjectData => {
  let objectivesHtml = ''
  if (typeof apiResponse.objectives === 'string') {
    objectivesHtml = `
      <p><strong>${t('new_project_page.project_objectives_label')}</strong></p>
      <p>${apiResponse.objectives}</p>
    `
  } else if (Array.isArray(apiResponse.objectives)) {
    objectivesHtml = `
      <p><strong>${t('new_project_page.project_objectives_label')}</strong></p>
      <ul>
        ${apiResponse.objectives.map(obj => `<li>${obj}</li>`).join('')}
      </ul>
    `
  } else {
    objectivesHtml = `<p><strong>${t('new_project_page.project_objectives_label')}</strong></p><p>Objetivos não disponíveis</p>`
  }

  let stagesHtml = ''
  if (Array.isArray(apiResponse.stages)) {
    if (apiResponse.stages.length > 0 && typeof apiResponse.stages[0] === 'object' && 'description' in apiResponse.stages[0]) {
      stagesHtml = apiResponse.stages.map((stageObj: any) => `
        <p><strong>${t('new_project_page.stage_label')} ${stageObj.stage}:</strong></p>
        <p>${stageObj.description}</p>
      `).join('')
    } else {
      stagesHtml = apiResponse.stages.map((stage: any, index) => `
        <p><strong>${t('new_project_page.stage_label')} ${index + 1}:</strong></p>
        <p>${stage}</p>
      `).join('')
    }
  } else {
    stagesHtml = '<p><strong>Etapas:</strong></p><p>Etapas não disponíveis</p>'
  }

  const mapFinalProductKind = (kind: string): string => {
    const kindMap: { [key: string]: string } = {
      'publication': 'publication',
      'presentation': 'presentation',
      'prototype': 'prototype',
      'performance': 'performance',
      'event': 'event',
      'media': 'media',
      'report': 'report',
      'product': 'other',
      'book': 'publication',
      'manual': 'publication',
      'guide': 'publication'
    }
    return kindMap[kind] || 'other'
  }

  return {
    name: apiResponse.name || 'Projeto sem nome',
    purpose: apiResponse.purpose || 'Propósito não definido',
    synopsis: apiResponse.synopsis ? `<p>${apiResponse.synopsis}</p>` : '<p>Synopsis não disponível</p>',
    objectives: objectivesHtml,
    stages: stagesHtml,
    modality: 'project',
    finalProductKind: mapFinalProductKind(apiResponse.finalProductKind || apiResponse.final_product_kind || 'other'),
    finalProductDescription: apiResponse.finalProductDescription || apiResponse.final_product_description || 'Descrição do produto final não disponível',
    evaluationToolsAttributes: [1, 2]
  }
}

const extractGradesFromGroup = (groupLabel: string, curriculumData?: any): string => {
  const gradeMatches = groupLabel.match(/\d+º/g)
  if (gradeMatches && gradeMatches.length > 0) {
    return gradeMatches.join(', ')
  }

  const stageName = curriculumData?.course?.developmentStage?.name?.toLowerCase() ||
                    curriculumData?.course?.development_stage?.name?.toLowerCase()
  if (stageName) {
    if (stageName.includes('infantil')) {
      return 'Infantil'
    } else if (stageName.includes('fundamental')) {
      const fundamentalMatch = groupLabel.match(/fundamental\s*([I|II])/i)
      if (fundamentalMatch) {
        return fundamentalMatch[1] === 'I' ? '1º, 2º, 3º, 4º, 5º' : '6º, 7º, 8º, 9º'
      }
    }
  }

  return '5º, 6º'
}

const callAIGenerationAPILegacy = async (description: string, groupId: string, groupLabel?: string): Promise<AIApiResponse> => {
  const curriculumData = await getGroupCurriculum(groupId)

  if (!curriculumData) {
    throw new Error('Não foi possível obter dados do curriculum do grupo')
  }

  const skillCurriculumId = curriculumData.skillCurriculum?.id || curriculumData.skill_curriculum?.id
  const contentCurriculumId = curriculumData.contentCurriculum?.id || curriculumData.content_curriculum?.id

  if (!skillCurriculumId || !contentCurriculumId) {
    console.warn('IDs do curriculum não encontrados, usando valores padrão')
  }

  const requestBody: AIGenerationPayloadLegacy = {
    request: description,
    skill_curriculum_id: skillCurriculumId || '33417965-df84-434d-98b4-ff4531a219b3',
    content_curriculum_id: contentCurriculumId || 'b9c9dda7-3f39-415e-8cba-c1dfc4a22b1b',
    grades: ''
  }

  const backendResponse = await askAI(requestBody)

  if (!backendResponse) {
    throw new Error('Resposta vazia da API')
  }

  if (!backendResponse.message || !backendResponse.message.includes('successfully')) {
    const errorMsg = backendResponse.error || backendResponse.message || 'Erro na chamada da API'
    throw new Error(errorMsg)
  }

  const aiResponse = backendResponse.response

  if (!aiResponse || !aiResponse.name || !aiResponse.stages || !aiResponse.objectives) {
    console.error('Resposta da API em formato inesperado:', aiResponse)
    throw new Error('Formato de resposta da API inválido - campos obrigatórios ausentes')
  }

  return aiResponse as AIApiResponse
}

// ==================== FUNÇÕES ARC ====================

const isValidGroupData = (data: any): data is GetMyGroupsProps => {
  return (
    data &&
    typeof data.value === 'string' &&
    typeof data.label === 'string' &&
    (data.grades === undefined || Array.isArray(data.grades)) &&
    (data.cycles === undefined || Array.isArray(data.cycles))
  )
}

const convertApiResponseToFormDataArc = (apiResponse: AIApiResponse, t: (key: string) => string): GeneratedProjectData => {
  let objectivesHtml = ''
  if (typeof apiResponse.objectives === 'string') {
    objectivesHtml = `
      <p><strong>${t('new_project_page.project_objectives_label')}</strong></p>
      <p>${apiResponse.objectives}</p>
    `
  } else if (Array.isArray(apiResponse.objectives)) {
    objectivesHtml = `
      <p><strong>${t('new_project_page.project_objectives_label')}</strong></p>
      <ul>
        ${apiResponse.objectives.map(obj => `<li>${obj}</li>`).join('')}
      </ul>
    `
  } else {
    objectivesHtml = `<p><strong>${t('new_project_page.project_objectives_label')}</strong></p><p>${t('new_project_page.ai_fallback_objectives')}</p>`
  }

  let stagesHtml = ''
  if (Array.isArray(apiResponse.stages)) {
    if (apiResponse.stages.length > 0 && typeof apiResponse.stages[0] === 'object' && 'description' in apiResponse.stages[0]) {
      stagesHtml = apiResponse.stages.map((stageObj: any) => `
        <p><strong>${t('new_project_page.stage_label')} ${stageObj.stage}:</strong></p>
        <p>${stageObj.description}</p>
      `).join('')
    } else {
      stagesHtml = apiResponse.stages.map((stage: any, index) => `
        <p><strong>${t('new_project_page.stage_label')} ${index + 1}:</strong></p>
        <p>${stage}</p>
      `).join('')
    }
  } else {
    stagesHtml = `<p><strong>${t('new_project_page.stages_modal')}:</strong></p><p>${t('new_project_page.ai_fallback_stages')}</p>`
  }

  const mapFinalProductKind = (kind: string): string => {
    const kindMap: { [key: string]: string } = {
      'publication': 'publication',
      'presentation': 'presentation',
      'prototype': 'prototype',
      'performance': 'performance',
      'event': 'event',
      'media': 'media',
      'report': 'report',
      'product': 'other',
      'book': 'publication',
      'manual': 'publication',
      'guide': 'publication'
    }
    return kindMap[kind] || 'other'
  }

  return {
    name: apiResponse.name || t('new_project_page.project_name'),
    purpose: apiResponse.purpose || t('new_project_page.ai_fallback_purpose'),
    synopsis: apiResponse.synopsis ? `<p>${apiResponse.synopsis}</p>` : `<p>${t('new_project_page.ai_fallback_synopsis')}</p>`,
    objectives: objectivesHtml,
    stages: stagesHtml,
    modality: 'project',
    finalProductKind: mapFinalProductKind(apiResponse.finalProductKind || apiResponse.final_product_kind || 'other'),
    finalProductDescription: apiResponse.finalProductDescription || apiResponse.final_product_description || t('new_project_page.ai_fallback_final_product'),
    evaluationToolsAttributes: [1, 2]
  }
}

const callAIGenerationAPIArc = async (
  description: string,
  groupData: GetMyGroupsProps,
  t: (key: string) => string
): Promise<AIApiResponse> => {
  const curriculumData = await getGroupCurriculum(groupData.value)

  if (!curriculumData) {
    throw new Error(t('new_project_page.ai_error_curriculum_data'))
  }

  const skillCurriculumId = curriculumData.skillCurriculum?.id || curriculumData.skill_curriculum?.id
  const contentCurriculumId = curriculumData.contentCurriculum?.id || curriculumData.content_curriculum?.id

  if (!skillCurriculumId || !contentCurriculumId) {
    console.warn(t('new_project_page.ai_warn_curriculum_ids'))
  }

  const grades = groupData.grades ?? []
  const cycles = groupData.cycles ?? []

  const requestBody: AIGenerationPayload = {
    request: description,
    skill_curriculum_id: skillCurriculumId || '33417965-df84-434d-98b4-ff4531a219b3',
    content_curriculum_id: contentCurriculumId || 'b9c9dda7-3f39-415e-8cba-c1dfc4a22b1b',
    grades: grades,
    cycles: cycles
  }

  const backendResponse = await askAIArc(requestBody)

  if (!backendResponse) {
    throw new Error(t('new_project_page.ai_error_empty_response'))
  }

  if (!backendResponse.message || !backendResponse.message.includes('successfully')) {
    const errorMsg = backendResponse.error || backendResponse.message || t('new_project_page.ai_error_api_call')
    throw new Error(errorMsg)
  }

  const aiResponse = backendResponse.response

  if (!aiResponse || !aiResponse.name || !aiResponse.stages || !aiResponse.objectives) {
    console.error('Resposta da API em formato inesperado:', aiResponse)
    throw new Error(t('new_project_page.ai_error_invalid_format'))
  }

  return aiResponse as AIApiResponse
}

// ==================== HOOK PRINCIPAL ====================

export function useAIGeneration() {
  const { t } = useArcTranslation()
  const arcNewProjectEnabled = useFeatureToggle('arc-mosaico-project')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generateProject = async (description: string, groupData?: any) => {
    setIsGenerating(true)
    setError(null)

    try {
      if (arcNewProjectEnabled) {
        // Versão Arc
        if (!isValidGroupData(groupData)) {
          throw new Error(t('new_project_page.ai_error_invalid_group_data'))
        }

        const apiResponse = await callAIGenerationAPIArc(description, groupData, t)
        const formData = convertApiResponseToFormDataArc(apiResponse, t)

        return { formData, apiResponse }
      } else {
        // Versão Legacy (main)
        const groupId = groupData?.value
        const groupLabel = groupData?.label

        if (!groupId) {
          throw new Error('ID do grupo não fornecido')
        }

        const apiResponse = await callAIGenerationAPILegacy(description, groupId, groupLabel)
        const formData = convertApiResponseToFormDataLegacy(apiResponse, t)

        return { formData, apiResponse }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao gerar projeto'
      setError(errorMessage)
      throw err
    } finally {
      setIsGenerating(false)
    }
  }

  return {
    generateProject,
    isGenerating,
    error
  }
}
